import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Divider,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  AccessTime as AccessTimeIcon,
  EventAvailable as EventAvailableIcon,
  CalendarToday as CalendarTodayIcon
} from '@mui/icons-material';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';

dayjs.locale('fr');

const daysOfWeek = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const daysOfWeekFr = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

const MobileBookingCalendar = ({ 
  salon, 
  selectedService, 
  onTimeSlotSelect, 
  selectedDateTime 
}) => {
  const [currentWeek, setCurrentWeek] = useState(dayjs().startOf('isoWeek'));
  const [availableSlots, setAvailableSlots] = useState({});
  const [existingBookings, setExistingBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [step, setStep] = useState('date'); // 'date' or 'time'
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(dayjs().month());
  const [selectedYear, setSelectedYear] = useState(dayjs().year());

  useEffect(() => {
    if (salon && selectedService) {
      fetchExistingBookings();
    } else if (salon && !selectedService) {
      generateAvailableSlots([]);
    }
  }, [salon, selectedService, currentWeek]);

  const fetchExistingBookings = async () => {
    setLoading(true);
    try {
      const weekStart = currentWeek.toDate();
      const weekEnd = currentWeek.add(6, 'day').endOf('day').toDate();

      const q = query(
        collection(db, 'bookings'),
        where('salonId', '==', salon.id),
        where('date', '>=', weekStart),
        where('date', '<=', weekEnd)
      );

      const querySnapshot = await getDocs(q);
      const bookings = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const bookingDate = data.date?.toDate ? data.date.toDate() : new Date(data.date);
        
        if (data.status === 'confirmed' || data.status === 'pending') {
          bookings.push({
            id: doc.id,
            ...data,
            date: bookingDate
          });
        }
      });

      setExistingBookings(bookings);
      generateAvailableSlots(bookings);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      setExistingBookings([]);
      generateAvailableSlots([]);
    } finally {
      setLoading(false);
    }
  };

  const generateAvailableSlots = (bookings) => {
    const slots = {};
    const serviceDuration = selectedService ? parseInt(selectedService.duration) : 60;
    const maxAdvanceDays = salon.bookingSettings?.maxAdvanceDays || 30;
    const maxDate = dayjs().add(maxAdvanceDays, 'day');

    for (let i = 0; i < 7; i++) {
      const date = currentWeek.add(i, 'day');
      const dateStr = date.format('YYYY-MM-DD');
      
      let dayIndex = date.day();
      let dayKey;
      if (dayIndex === 0) {
        dayKey = 'sunday';
      } else {
        dayKey = daysOfWeek[dayIndex - 1];
      }

      // Exclure les dates passées ET les dates au-delà de la limite de réservation
      if (date.isBefore(dayjs(), 'day') || date.isAfter(maxDate, 'day')) {
        slots[dateStr] = [];
        continue;
      }

      if (!salon.schedule) {
        slots[dateStr] = [];
        continue;
      }

      const daySchedule = salon.schedule[dayKey];
      
      if (!daySchedule || daySchedule.closed) {
        slots[dateStr] = [];
        continue;
      }

      const openTime = dayjs(`${dateStr} ${daySchedule.open}`);
      const closeTime = dayjs(`${dateStr} ${daySchedule.close}`);
      
      if (!openTime.isValid() || !closeTime.isValid()) {
        slots[dateStr] = [];
        continue;
      }

      const daySlots = [];
      let currentSlot = openTime;
      let slotCount = 0;
      
      while (currentSlot.isBefore(closeTime) && slotCount < 20) {
        const slotEnd = currentSlot.add(serviceDuration, 'minute');
        
        if (slotEnd.isAfter(closeTime)) {
          break;
        }
        
        const isBooked = bookings.some(booking => {
          const bookingStart = dayjs(booking.date);
          const bookingEnd = bookingStart.add(booking.duration || serviceDuration, 'minute');
          
          return (
            currentSlot.isBefore(bookingEnd) && 
            slotEnd.isAfter(bookingStart)
          );
        });

        // Skip slots that are too close to current time based on salon settings
        const minAdvanceBooking = salon.bookingSettings?.minAdvanceHours || 2; // Default 2 hours
        const isToday = currentSlot.isSame(dayjs(), 'day');
        const isTooSoon = isToday && currentSlot.isBefore(dayjs().add(minAdvanceBooking, 'hour'));

        if (!isBooked && !isTooSoon) {
          daySlots.push({
            time: currentSlot.format('HH:mm'),
            datetime: currentSlot.toDate(),
            available: true
          });
        }

        currentSlot = currentSlot.add(30, 'minute');
        slotCount++;
      }

      slots[dateStr] = daySlots;
    }

    setAvailableSlots(slots);
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setStep('time');
  };

  const handleTimeSelect = (slot) => {
    if (typeof onTimeSlotSelect === 'function') {
      onTimeSlotSelect(slot.datetime);
      // Ne pas revenir automatiquement à l'étape de date
      // L'utilisateur peut voir sa sélection et revenir manuellement s'il le souhaite
    } else {
      console.error('onTimeSlotSelect is not a function:', onTimeSlotSelect);
    }
  };

  const handleBackToDate = () => {
    setStep('date');
    setSelectedDate(null);
  };

  const goToPreviousWeek = () => {
    const previousWeek = currentWeek.subtract(1, 'week');
    // Empêcher de revenir à une semaine qui contient des dates passées
    if (previousWeek.add(6, 'day').isBefore(dayjs(), 'day')) {
      return; // Ne pas permettre de naviguer vers une semaine entièrement passée
    }
    setCurrentWeek(previousWeek);
    setStep('date');
    setSelectedDate(null);
  };

  const goToNextWeek = () => {
    const maxAdvanceDays = salon.bookingSettings?.maxAdvanceDays || 30;
    const maxDate = dayjs().add(maxAdvanceDays, 'day');
    const nextWeek = currentWeek.add(1, 'week');
    
    // Empêcher de naviguer au-delà de la limite de réservation en avance
    if (nextWeek.isAfter(maxDate, 'day')) {
      return; // Ne pas permettre de naviguer au-delà de la limite
    }
    setCurrentWeek(nextWeek);
    setStep('date');
    setSelectedDate(null);
  };

  const getAvailableDates = () => {
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = currentWeek.add(i, 'day');
      const dateStr = date.format('YYYY-MM-DD');
      const slots = availableSlots[dateStr] || [];
      
      // Ne garder que les jours avec des créneaux disponibles
      if (slots.length > 0) {
        dates.push({
          date,
          dateStr,
          hasSlots: true,
          slotsCount: slots.length
        });
      }
    }
    return dates;
  };

  const handleDatePickerOpen = () => {
    setSelectedMonth(currentWeek.month());
    setSelectedYear(currentWeek.year());
    setDatePickerOpen(true);
  };

  const handleDatePickerClose = () => {
    setDatePickerOpen(false);
  };

  const handleQuickDateSelect = () => {
    const maxAdvanceDays = salon.bookingSettings?.maxAdvanceDays || 30;
    const maxDate = dayjs().add(maxAdvanceDays, 'day');
    const newDate = dayjs().month(selectedMonth).year(selectedYear).startOf('month');
    
    // Vérifier si la date sélectionnée est dans les limites
    if (newDate.isBefore(dayjs(), 'month') || newDate.isAfter(maxDate, 'month')) {
      return; // Ne pas permettre de naviguer en dehors des limites
    }
    
    setCurrentWeek(newDate.startOf('isoWeek'));
    setStep('date');
    setSelectedDate(null);
    setDatePickerOpen(false);
  };

  const getAvailableYears = () => {
    const currentYear = dayjs().year();
    const maxAdvanceDays = salon.bookingSettings?.maxAdvanceDays || 30;
    const maxYear = dayjs().add(maxAdvanceDays, 'day').year();
    const years = [];
    
    for (let year = currentYear; year <= maxYear; year++) {
      years.push(year);
    }
    return years;
  };

  const getAvailableMonths = () => {
    const currentYear = dayjs().year();
    const currentMonth = dayjs().month();
    const maxAdvanceDays = salon.bookingSettings?.maxAdvanceDays || 30;
    const maxDate = dayjs().add(maxAdvanceDays, 'day');
    const months = [];
    
    for (let month = 0; month < 12; month++) {
      const testDate = dayjs().month(month).year(selectedYear);
      
      if (selectedYear === currentYear && month < currentMonth) {
        continue; // Skip past months in current year
      }
      
      if (testDate.isAfter(maxDate, 'month')) {
        break; // Stop if beyond max advance date
      }
      
      months.push({
        value: month,
        label: dayjs().month(month).format('MMMM')
      });
    }
    return months;
  };

  if (!selectedService) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        Veuillez d'abord sélectionner un service
      </Alert>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      {step === 'date' ? (
        // Date Selection Step
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <IconButton
              onClick={goToPreviousWeek}
              size="small"
              disabled={currentWeek.subtract(1, 'week').add(6, 'day').isBefore(dayjs(), 'day')}
              sx={{
                '&.Mui-disabled': {
                  opacity: 0.3
                }
              }}
            >
              <ChevronLeftIcon />
            </IconButton>
            <Box sx={{ textAlign: 'center' }}>
              <Button
                onClick={handleDatePickerOpen}
                sx={{
                  textTransform: 'none',
                  color: '#000000',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.04)'
                  }
                }}
                startIcon={<CalendarTodayIcon fontSize="small" />}
              >
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {currentWeek.format('MMMM YYYY')}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '0.75rem' }}>
                    {currentWeek.format('DD MMM')} - {currentWeek.add(6, 'day').format('DD MMM')}
                  </Typography>
                </Box>
              </Button>
            </Box>
            <IconButton
              onClick={goToNextWeek}
              size="small"
              disabled={currentWeek.add(1, 'week').isAfter(dayjs().add(salon.bookingSettings?.maxAdvanceDays || 30, 'day'), 'day')}
              sx={{
                '&.Mui-disabled': {
                  opacity: 0.3
                }
              }}
            >
              <ChevronRightIcon />
            </IconButton>
          </Box>

          <Typography variant="subtitle2" sx={{ mb: 2, color: '#6B7280', textAlign: 'center' }}>
            Sélectionnez une date
          </Typography>

          <Grid container spacing={1}>
            {getAvailableDates().map(({ date, dateStr, hasSlots, slotsCount }) => (
              <Grid item xs={12} key={dateStr}>
                <Button
                  fullWidth
                  variant={hasSlots ? "outlined" : "text"}
                  disabled={!hasSlots}
                  onClick={() => handleDateSelect(date)}
                  sx={{
                    py: 2,
                    borderRadius: 2,
                    border: hasSlots ? '2px solid #E5E7EB' : 'none',
                    backgroundColor: hasSlots ? '#FFFFFF' : '#F8F9FA',
                    color: hasSlots ? '#000000' : '#9CA3AF',
                    '&:hover': {
                      backgroundColor: hasSlots ? '#F8F9FA' : '#F8F9FA',
                      borderColor: hasSlots ? '#6B7280' : '#E5E7EB'
                    },
                    '&:disabled': {
                      backgroundColor: '#F8F9FA',
                      color: '#9CA3AF'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {date.format('dddd DD MMMM')}
                      </Typography>
                      {hasSlots && (
                        <Typography variant="caption" sx={{ color: '#6B7280' }}>
                          {slotsCount} créneaux disponibles
                        </Typography>
                      )}
                    </Box>
                    {hasSlots && (
                      <Chip 
                        size="small" 
                        onClick={() => {}}
                        label={slotsCount}
                        sx={{ 
                          backgroundColor: '#F5F5DC', 
                          color: '#000000',
                          fontWeight: 500
                        }}
                      />
                    )}
                  </Box>
                </Button>
              </Grid>
            ))}
          </Grid>
        </>
      ) : (
        // Time Selection Step
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <IconButton onClick={handleBackToDate} size="small" sx={{ mr: 2 }}>
              <ChevronLeftIcon />
            </IconButton>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {selectedDate?.format('dddd DD MMMM')}
              </Typography>
              <Typography variant="caption" sx={{ color: '#6B7280' }}>
                Sélectionnez un horaire
              </Typography>
            </Box>
          </Box>

          <Grid container spacing={2}>
            {(availableSlots[selectedDate?.format('YYYY-MM-DD')] || []).map((slot, index) => (
              <Grid item xs={6} key={index}>
                <Button
                  fullWidth
                  variant={selectedDateTime && dayjs(selectedDateTime).format('HH:mm') === slot.time ? "contained" : "outlined"}
                  onClick={() => handleTimeSelect(slot)}
                  sx={{
                    py: 2,
                    borderRadius: 2,
                    backgroundColor: selectedDateTime && dayjs(selectedDateTime).format('HH:mm') === slot.time ? '#000000' : '#FFFFFF',
                    color: selectedDateTime && dayjs(selectedDateTime).format('HH:mm') === slot.time ? '#FFFFFF' : '#000000',
                    border: '2px solid #E5E7EB',
                    '&:hover': {
                      backgroundColor: selectedDateTime && dayjs(selectedDateTime).format('HH:mm') === slot.time ? '#1F1F1F' : '#F8F9FA',
                      borderColor: '#6B7280'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccessTimeIcon fontSize="small" />
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {slot.time}
                    </Typography>
                  </Box>
                </Button>
              </Grid>
            ))}
          </Grid>

          {selectedDateTime && (
            <Box sx={{ mt: 3, p: 2, backgroundColor: '#F0FDF4', borderRadius: 2, border: '1px solid #BBF7D0' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EventAvailableIcon sx={{ color: '#16a34a', fontSize: '1.25rem' }} />
                <Typography variant="body2" sx={{ color: '#15803d', fontWeight: 500 }}>
                  Créneau sélectionné: {selectedDate?.format('dddd DD MMMM')} à {dayjs(selectedDateTime).format('HH:mm')}
                </Typography>
              </Box>
            </Box>
          )}
        </>
      )}

      {/* Dialog de sélection rapide de date */}
      <Dialog open={datePickerOpen} onClose={handleDatePickerClose} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Aller à un mois spécifique
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Année</InputLabel>
              <Select
                value={selectedYear}
                label="Année"
                onChange={(e) => setSelectedYear(e.target.value)}
              >
                {getAvailableYears().map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl fullWidth>
              <InputLabel>Mois</InputLabel>
              <Select
                value={selectedMonth}
                label="Mois"
                onChange={(e) => setSelectedMonth(e.target.value)}
              >
                {getAvailableMonths().map((month) => (
                  <MenuItem key={month.value} value={month.value}>
                    {month.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDatePickerClose} sx={{ color: '#6B7280' }}>
            Annuler
          </Button>
          <Button
            onClick={handleQuickDateSelect}
            variant="contained"
            sx={{
              backgroundColor: '#000000',
              color: '#FFFFFF',
              '&:hover': { backgroundColor: '#1F1F1F' }
            }}
          >
            Aller à cette date
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MobileBookingCalendar;